import { useState, type ReactNode } from 'react';
import { Settings as SettingsIcon } from '@ui/shared/icons';
import { ThemeSwitcher } from '@ui/desktop/rewards/ThemeSwitcher';
import { getStoredOwnerName, setOwnerName } from '@ui/shared/settings/ownerName';
import { getStoredLocation, setLocation } from '@ui/shared/settings/location';
import { CITIES } from '@ui/shared/format/cities';
import { ExportSection } from './ExportSection';
import { ImportFlow } from './ImportFlow';

/**
 * `SettingsScreen` — desktop-only, no phone entry point this round
 * (settings-rewards.md §5a, phase-2e-nav-settings-export.md ST-1/ST-2/ST-3).
 * Only ever rendered inside `DesktopShell`; App.tsx is the single call site.
 *
 * Three sections in order (§5a.2): Data (export/import), Preferences (week
 * convention, reduced motion, theme), About (version + jump-to-export
 * link). `ThemeSwitcher` is relocated here from `RewardsScreenDesktop`
 * (§5a.4) — same component, same internals, only its call site and
 * surrounding section wrapper move.
 *
 * Widened from 720px to a 1600px shell (Today/Habits/Objectives/Weekly-
 * Review desktop-consistency pass), restructured into the same two-column
 * shape as those screens — main content (Data + Preferences) on the left,
 * a sticky About card on the right — rather than just a single wider
 * column. The left column's own reading width is still capped
 * (`--settings-content-max-width` below) since Preferences' prose
 * ("Weeks run Monday to Sunday...") reads badly stretched past a
 * comfortable line length; letting the SHELL span 1600px like every other
 * desktop screen while the actual form/prose content keeps a sane width is
 * the same move Weekly Review made (widen the shell, not the paragraph).
 * Each section is a proper `--surface-raised` card (matching every other
 * panel in the app) instead of plain stacked text.
 */
const SETTINGS_CONTENT_MAX_WIDTH = '640px';

export function SettingsScreen() {
  const [ownerName, setOwnerNameState] = useState(() => getStoredOwnerName());
  const [locationName, setLocationNameState] = useState(() => getStoredLocation()?.name ?? '');

  return (
    <main
      aria-label="Settings"
      style={{
        padding: 'var(--space-8) var(--layout-page-margin-desktop) var(--space-16)',
        maxWidth: '1600px',
        margin: '0 auto',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <SettingsIcon size={22} color="var(--hue-primary-vivid)" aria-hidden="true" />
        <h1 style={{ margin: 0, fontSize: 'var(--type-display-size)', fontWeight: 'var(--type-display-weight)', color: 'var(--text-primary)' }}>
          Settings
        </h1>
      </header>
      <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
        Your data, your theme, your week — all in one place.
      </p>

      <div style={{ marginTop: 'var(--layout-section-gap)', display: 'grid', gridTemplateColumns: `minmax(0, ${SETTINGS_CONTENT_MAX_WIDTH}) 380px`, gap: 'var(--space-6)', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--layout-desktop-panel-gap)', minWidth: 0 }}>
          <SettingsCard>
            <SectionHeading>Data</SectionHeading>
            <div style={{ marginTop: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div id="settings-export">
                <ExportSection />
              </div>
              <ImportFlow />
            </div>
          </SettingsCard>

          <SettingsCard>
            <SectionHeading>Preferences</SectionHeading>
            <div style={{ marginTop: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
                Weeks run Monday to Sunday. The review opens Sunday.
              </p>
              <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
                Compass follows your system&apos;s reduced-motion preference.
              </p>
              <div>
                <label
                  htmlFor="settings-owner-name"
                  style={{
                    display: 'block',
                    fontSize: 'var(--type-caption-size)',
                    color: 'var(--text-muted)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  Your name
                </label>
                <input
                  id="settings-owner-name"
                  type="text"
                  value={ownerName}
                  placeholder="Not set"
                  onChange={(e) => {
                    const next = e.target.value;
                    setOwnerNameState(next);
                    setOwnerName(next);
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '320px',
                    minHeight: 'var(--touch-target-min)',
                    padding: '0 var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--surface-sunken)',
                    border: '1px solid var(--border-subtle)',
                    color: 'var(--text-primary)',
                    fontSize: 'var(--type-body-size)',
                  }}
                />
                <p style={{ margin: 0, marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
                  Used only for the Dashboard greeting — never synced.
                </p>
              </div>
              <div>
                <label
                  htmlFor="settings-location"
                  style={{
                    display: 'block',
                    fontSize: 'var(--type-caption-size)',
                    color: 'var(--text-muted)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  City
                </label>
                <select
                  id="settings-location"
                  value={locationName}
                  onChange={(e) => {
                    const next = e.target.value;
                    setLocationNameState(next);
                    const city = CITIES.find((c) => c.name === next);
                    setLocation(
                      city ? { name: city.name, lat: city.lat, long: city.long, timezone: city.timezone } : null,
                    );
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '320px',
                    minHeight: 'var(--touch-target-min)',
                    padding: '0 var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--surface-sunken)',
                    border: '1px solid var(--border-subtle)',
                    color: 'var(--text-primary)',
                    fontSize: 'var(--type-body-size)',
                  }}
                >
                  <option value="">Not set</option>
                  {CITIES.map((city) => (
                    <option key={city.name} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
                <p style={{ margin: 0, marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
                  Used for the Today screen&apos;s sunrise/sunset times — never synced.
                </p>
              </div>
              <ThemeSwitcher />
            </div>
          </SettingsCard>
        </div>

        <div style={{ position: 'sticky', top: 'var(--space-8)' }}>
          <SettingsCard>
            <SectionHeading>About</SectionHeading>
            <div style={{ marginTop: 'var(--space-4)' }}>
              <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>Compass v1.0</p>
              <a
                href="#settings-export"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('settings-export')?.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{
                  display: 'inline-block',
                  marginTop: 'var(--space-2)',
                  color: 'var(--hue-primary)',
                  fontWeight: 600,
                  fontSize: 'var(--type-caption-size)',
                  minHeight: 'var(--touch-target-min)',
                }}
              >
                Jump to export
              </a>
            </div>
          </SettingsCard>
        </div>
      </div>
    </main>
  );
}

function SettingsCard({ children }: { readonly children: ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--layout-card-padding-desktop)',
      }}
    >
      {children}
    </section>
  );
}

function SectionHeading({ children }: { readonly children: string }) {
  return (
    <h2
      style={{
        margin: 0,
        fontSize: 'var(--type-heading-size)',
        fontWeight: 'var(--type-heading-weight)',
        color: 'var(--text-primary)',
      }}
    >
      {children}
    </h2>
  );
}
